import React, {useCallback, useEffect, useState, useMemo} from 'react';
import {debounce} from 'ts-debounce';

import {assert} from 'Asserts';
import {isInaccessibleInfo} from 'auth/RequestAccess';
import BenchlingConfig from 'BenchlingConfig';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {RequestV2StatusType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import DocumentActions from 'notebook/documents/DocumentActions';
import DocumentEditor from 'notebook/documents/DocumentEditor';
import DocumentStore from 'notebook/documents/DocumentStore';
import {invokeStructureTableValidation} from 'notebook/structured-tables/action-functions/validateStructuredTableActionFunction';
import {StructuredTableInsertionContext} from 'notebook/structured-tables/util/StructuredTableInsertionContext';
import notifications from 'notifications';
import RequestV2SubmissionHeader, {
  updateRequestV2Submission,
} from 'processes/requests_v2/request-v2-submission/RequestV2SubmissionHeader';
import {
  getTaskTableFromRequestDocument,
  isRequestSubmissionLocked,
  isSendDisabledForRequest,
  isTaskTableBlockingSubmission,
} from 'processes/requests_v2/request-v2-submission/RequestV2SubmissionUtils';
import RealtimeUpdateSubscriptionWrapper from 'realtime/RealtimeUpdateSubscriptionWrapper';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import TabLayout, {TabLayoutBackgroundColor} from 'ui-library/TabLayout';
import {typeToName} from 'util/NameUtil';

import {
  RequestV2SubmissionDetailsQuery,
  RequestV2SubmissionDetailsQueryVariables,
} from './__generated/RequestV2SubmissionDetailsQuery';
import {
  REQUEST_V2_SUBMISSION_DETAILS_QUERY,
  RequestV2SubmissionDetails,
} from './RequestV2SubmissionDetailsQueryUtils';

interface QueryingRequestV2SubmissionDocumentProps {
  requestV2SubmissionId: string;
  onSubmissionAddProgressAndOutputsTab: () => void;
}

interface RequestV2SubmissionDocumentProps {
  requestV2Submission: RequestV2SubmissionDetails;
  onSubmissionAddProgressAndOutputsTab: () => void;
}

function renderRequestV2SubmissionBanner(
  isLockedByPermissions: boolean,
  statusType: RequestV2StatusType,
  isArchived: boolean,
  isWritable: boolean
): JSX.Element | null {
  if (isArchived) {
    return <>Editing is disabled on archived requests.</>;
  } else if (statusType === RequestV2StatusType.SENT) {
    return <>The request has been sent and cannot be edited.</>;
  } else if (statusType === RequestV2StatusType.DISCARDED) {
    // Unarchived requests are still in a discarded state.
    return <>Editing is disabled on previously archived requests.</>;
  } else if (!isWritable || isLockedByPermissions) {
    return <>You have insufficient permissions to edit this request.</>;
  }
  return null;
}

function shouldDisableSendButton(documentId: string): boolean {
  return isSendDisabledForRequest(documentId);
}

function RequestV2SubmissionDocumentBody(props: RequestV2SubmissionDocumentProps): JSX.Element {
  const [isSendDisabled, setIsSendDisabled] = useState(
    shouldDisableSendButton(props.requestV2Submission.document.id)
  );

  // Correctly open the document + disable or enable the send button initially
  useEffect(() => {
    const openDocument = async (): Promise<void> => {
      const document = DocumentStore.getDocument(props.requestV2Submission.document.id);
      if (!document || document.isStale) {
        await DocumentActions.open(props.requestV2Submission.document.id);
        setIsSendDisabled(shouldDisableSendButton(props.requestV2Submission.document.id));
      }
    };
    openDocument();
  }, [props.requestV2Submission.document.id]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onDocumentChange = useCallback(
    debounce(
      () => {
        setIsSendDisabled(shouldDisableSendButton(props.requestV2Submission.document.id));
      },
      500,
      {maxWait: 10000}
    ),
    [props.requestV2Submission.document.id]
  );

  const structuredTableInsertionContextValue = useMemo(
    () => ({itemInsertedFromId: props.requestV2Submission.id}),
    [props.requestV2Submission.id]
  );

  const isRequestLocked = isRequestSubmissionLocked(props.requestV2Submission);

  return (
    <TabLayout
      header={
        <RequestV2SubmissionHeader
          requestV2Submission={props.requestV2Submission}
          isSendDisabled={isSendDisabled}
          isRequestLocked={isRequestLocked}
          onSend={async () => {
            // Run one hard check before sending the request, since we're debouncing
            if (shouldDisableSendButton(props.requestV2Submission.document.id)) {
              notifications.error(
                'Could not send request due to errors or unsubmitted changes in the task table. Please try again.'
              );
            } else {
              const tables = getTaskTableFromRequestDocument(props.requestV2Submission.document.id);
              assert(tables != null, 'Expected to find a task table in the document');
              const {taskTable, computedTable} = tables;
              if (isTaskTableBlockingSubmission(taskTable, computedTable)) {
                notifications.error(
                  'There are errors in the task table. Please fix them before sending the request.'
                );
                return;
              }
              const isTableValid = await invokeStructureTableValidation({
                entryOrTemplateId: props.requestV2Submission.document.id,
                structuredTable: taskTable,
                computedTable,
              });
              if (!isTableValid) {
                return;
              }

              await updateRequestV2Submission({
                id: props.requestV2Submission.id,
                status: RequestV2StatusType.SENT,
                loadingScreenText: 'Sending request...',
              });
              if (BenchlingConfig.REQUESTS_V2_MVP_ENABLE) {
                props.onSubmissionAddProgressAndOutputsTab();
              }
            }
          }}
        />
      }
      backgroundColor={TabLayoutBackgroundColor.WHITE}
      dataTestAttrs={{component: 'RequestV2SubmissionDocumentBody', element: 'tabLayout'}}
    >
      <StructuredTableInsertionContext.Provider value={structuredTableInsertionContextValue}>
        <DocumentEditor
          renderBanner={(isLockedByPermissions: boolean) =>
            renderRequestV2SubmissionBanner(
              isLockedByPermissions,
              props.requestV2Submission.status,
              props.requestV2Submission.archiveData.archived,
              props.requestV2Submission.permissions.writable
            )
          }
          onChange={onDocumentChange}
          id={props.requestV2Submission.document.id}
          locked={isRequestLocked}
          hideCanvasTitle={true}
          isVirtualizationEnabled={true}
          fileTypeName={typeToName(ModelTypeConstants.REQUEST_V2_SUBMISSION)}
          manageRealtimeUpdates={true}
        />
      </StructuredTableInsertionContext.Provider>
    </TabLayout>
  );
}

export default function QueryingRequestV2SubmissionDocument(
  props: QueryingRequestV2SubmissionDocumentProps
): JSX.Element | null {
  const result = useBenchlingQuery<RequestV2SubmissionDetailsQuery, RequestV2SubmissionDetailsQueryVariables>(
    REQUEST_V2_SUBMISSION_DETAILS_QUERY,
    {
      variables: {requestV2SubmissionId: props.requestV2SubmissionId},
      fetchPolicy: 'cache-and-network',
      renderCachedDataWhenRefetching: true,
    }
  );
  if (result.type === QueryResultType.PENDING) {
    return <CenteredHorizontalThrobber />;
  } else if (result.type === QueryResultType.ERROR || isInaccessibleInfo(result.data.requestV2Submission)) {
    notifications.error('Could not load request; try reloading the page.', {id: 'requestV2Error'});
    return null;
  }

  return (
    <RealtimeUpdateSubscriptionWrapper
      apiId={props.requestV2SubmissionId}
      refetch={() => {
        result.refetch();
      }}
    >
      <RequestV2SubmissionDocumentBody
        requestV2Submission={result.data.requestV2Submission}
        onSubmissionAddProgressAndOutputsTab={props.onSubmissionAddProgressAndOutputsTab}
      />
    </RealtimeUpdateSubscriptionWrapper>
  );
}
